/**
 * BomberDrone.ts
 * Pooled hostile bomber for the World 2 space shmup.
 *
 * Behaviour: linear leftward drift in world space at half the fighter
 * speed, with 2 HP, and periodic cluster-bomb drops. Spawned at the
 * right edge of the camera, drifts past the ship dropping bombs on a
 * fixed cadence (gated on the bomber being inside the viewport so
 * the off-screen spawn margin isn't a free bomb), and despawns once
 * its right edge crosses the `SPACE_ENEMY_OFFSCREEN_MARGIN` past the
 * left edge of the camera.
 *
 * The drop pool + lifecycle live in `bomberDroneDrop` — this file
 * just imports `dropClusterBomb` and honours the pool-miss return
 * contract by leaving its cadence timer parked on a miss.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module BomberDrone
 */

import { SpaceEnemyBase } from './SpaceEnemyBase';
import { spaceState } from '../engine/spaceState';
import type { LevelRuntime } from '../engine/gameState';
import { dropClusterBomb } from '../engine/bomberDroneDrop';
import {
  BOMBER_DRONE_WIDTH,
  BOMBER_DRONE_HEIGHT,
  BOMBER_DRONE_HP,
  BOMBER_DRONE_DRIFT_SPEED,
  BOMBER_DRONE_WAVE_AMPLITUDE_PX,
  BOMBER_DRONE_WAVE_FREQUENCY_HZ,
  BOMBER_DRONE_BOMB_WIDTH,
  BOMBER_DRONE_DROP_INTERVAL_S,
  BOMBER_DRONE_DROP_INITIAL_DELAY_S,
  SPACE_ENEMY_OFFSCREEN_MARGIN,
  type SpaceEnemyKind,
} from '../../constants/spaceEnemyConstants';
import { VIEWPORT_WIDTH } from '../../constants/gameConstants';

/**
 * Slow, heavy left-drifting bomber drone with a gentle vertical wobble.
 * Pooled — use after `acquire()` from the bomber pool in
 * `spaceEnemySystem`.
 */
export class BomberDrone extends SpaceEnemyBase {
  /** Discriminator read by the renderer to pick a colour / sprite. */
  public readonly kind: SpaceEnemyKind = 'bomber';

  /** Per-spawn phase offset (radians) so a wave of bombers doesn't sync. */
  private phase = 0;
  /** Local time accumulator for the sine driver. */
  private localT = 0;
  /**
   * Seconds remaining until the next bomb drop. Seeded by `spawn()`
   * with `BOMBER_DRONE_DROP_INITIAL_DELAY_S` plus a per-drone phase
   * offset so a wave of bombers doesn't sync-drop. Reset to
   * `BOMBER_DRONE_DROP_INTERVAL_S` after each successful drop, and
   * left parked at ≤ 0 on a pool miss so the bomber retries the next
   * tick rather than burning a full cadence on a dropped spawn.
   */
  private dropTimer = 0;

  constructor() {
    super(BOMBER_DRONE_WIDTH, BOMBER_DRONE_HEIGHT);
  }

  /**
   * Initialise the drone at a world position. Marks it live and assigns a
   * fresh `spawnId` via the base class.
   *
   * @param x - Spawn X (world space, top-left of hitbox).
   * @param y - Spawn Y baseline (world space, top-left of hitbox).
   */
  public spawn(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.vx = -BOMBER_DRONE_DRIFT_SPEED;
    this.vy = 0;
    this.hp = BOMBER_DRONE_HP;
    this.markSpawned();
    this.phase = (this.spawnId % 16) * (Math.PI / 8);
    // DECISION: Offset `localT` so the first sine sample is zero (mirror
    // of FighterDrone). Without this, the spawn `y` is not the wobble
    // centre and the bomber can drift up to ±2*amplitude from spawn,
    // defeating the `+ amplitude` spawn clamp (PR #124 review).
    this.localT = -this.phase / (2 * Math.PI * BOMBER_DRONE_WAVE_FREQUENCY_HZ);
    // DECISION: Seed the drop timer with the initial delay plus a
    // small per-spawn jitter (re-using `this.phase` as a free entropy
    // source bounded by ~0–2π) so a wave of bombers spawned the same
    // tick doesn't detonate on identical frames downstream — same
    // pattern as FighterDrone.fireTimer.
    this.dropTimer =
      BOMBER_DRONE_DROP_INITIAL_DELAY_S +
      (this.phase / (2 * Math.PI)) * BOMBER_DRONE_DROP_INTERVAL_S;
  }

  /**
   * Advance one fixed timestep. Linear X drift + sinusoidal Y delta applied
   * on top of current Y + camera-relative left cull. Mirrors
   * `FighterDrone.update()` so all space enemies leave the world at a
   * consistent x position.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused (space has no level geometry).
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    // DECISION: Sine-wave Y delta (mirror of FighterDrone) so external
    // y reassignments are honoured as a new drift centre.
    const prevAngle =
      2 * Math.PI * BOMBER_DRONE_WAVE_FREQUENCY_HZ * this.localT + this.phase;
    this.localT += dt;
    const nextAngle =
      2 * Math.PI * BOMBER_DRONE_WAVE_FREQUENCY_HZ * this.localT + this.phase;
    this.x += this.vx * dt;
    this.y +=
      (Math.sin(nextAngle) - Math.sin(prevAngle)) *
      BOMBER_DRONE_WAVE_AMPLITUDE_PX;

    const leftCullX = spaceState.cameraX - SPACE_ENEMY_OFFSCREEN_MARGIN;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
      return;
    }

    // Tick the drop cadence. Only drop once the bomber has drifted
    // INTO the viewport (its left edge is to the left of the camera's
    // right edge) so bombs spawned while still in the right-side
    // spawn margin don't fall from nowhere. The bomb spawns at the
    // bomber's bottom-centre so the payload reads as released from
    // the underside of the drone.
    this.dropTimer -= dt;
    if (this.dropTimer <= 0) {
      const visibleRightX = spaceState.cameraX + VIEWPORT_WIDTH;
      if (this.x < visibleRightX) {
        const dropped = dropClusterBomb(
          this.x + this.width / 2 - BOMBER_DRONE_BOMB_WIDTH / 2,
          this.y + this.height,
        );
        // Pool miss: leave the timer negative so we retry next tick
        // rather than burning a full cadence on a dropped drop.
        if (!dropped) return;
      }
      this.dropTimer = BOMBER_DRONE_DROP_INTERVAL_S;
    }
  }
}
